import { db } from './indexeddb'
import type { BackupData, BackupMetadata, ImportOptions, ImportResult } from './types'

/**
 * Backup, Export & Import Logic
 * Handles data export to JSON and import from backups
 */

/**
 * Export database to JSON
 */
export async function exportToJSON(userId?: string): Promise<BackupData> {
  // Get all data
  const [tasks, settings] = await Promise.all([
    userId
      ? db.tasks.filter((task) => task.userId === userId).toArray()
      : db.tasks.toArray(),
    db.settings.toArray(),
  ])

  // Create metadata
  const metadata: BackupMetadata = {
    version: '1.0',
    exportedAt: new Date(),
    platform: 'electron',
    userId,
    recordCount: {
      tasks: tasks.length,
      settings: settings.length,
    },
  }

  return {
    metadata,
    tasks: tasks as any[],
    settings: settings as any[],
  }
}

/**
 * Export to JSON file (trigger download)
 */
export async function exportToFile(userId?: string): Promise<void> {
  const data = await exportToJSON(userId)
  const json = JSON.stringify(data, null, 2)
  const blob = new Blob([json], { type: 'application/json' })
  const url = URL.createObjectURL(blob)

  // Create download link
  const link = document.createElement('a')
  link.href = url
  link.download = `happytask-backup-${new Date().toISOString().split('T')[0]}.json`
  document.body.appendChild(link)
  link.click()
  document.body.removeChild(link)
  URL.revokeObjectURL(url)
}

/**
 * Validate backup data structure
 */
function validateBackupData(data: any): data is BackupData {
  if (!data || typeof data !== 'object') return false
  if (!data.metadata || !data.tasks || !data.settings) return false
  if (!Array.isArray(data.tasks) || !Array.isArray(data.settings)) return false

  // Check metadata
  if (!data.metadata.version || !data.metadata.exportedAt) return false

  return true
}

/**
 * Import from JSON backup
 */
export async function importFromJSON(
  data: BackupData,
  options: ImportOptions = { strategy: 'merge' }
): Promise<ImportResult> {
  const result: ImportResult = {
    success: false,
    imported: {
      tasks: 0,
      settings: 0,
    },
    skipped: 0,
    errors: [],
  }

  try {
    // Validate data
    if (options.validateSchema !== false && !validateBackupData(data)) {
      throw new Error('Invalid backup data structure')
    }

    // Handle different import strategies
    if (options.strategy === 'replace') {
      // Clear existing data
      await db.tasks.clear()
      await db.settings.clear()
    }

    // Import tasks
    for (const task of data.tasks) {
      try {
        if (options.strategy === 'merge') {
          // Check if task already exists (by title + createdAt)
          const existing = await db.tasks
            .filter(
              (t) =>
                t.title === task.title &&
                t.createdAt.getTime() === new Date(task.createdAt).getTime()
            )
            .first()

          if (existing) {
            if (options.strategy === 'skip') {
              result.skipped++
              continue
            }
          }
        }

        // Convert date strings to Date objects
        const taskToImport = {
          ...task,
          createdAt: new Date(task.createdAt),
          updatedAt: new Date(task.updatedAt),
          syncedAt: task.syncedAt ? new Date(task.syncedAt) : null,
          deletedAt: task.deletedAt ? new Date(task.deletedAt) : null,
        }

        delete taskToImport.id // Let Dexie assign new UUID via creating hook
        await db.tasks.add(taskToImport)
        result.imported.tasks++
      } catch (error) {
        result.errors.push(`Task import error: ${error}`)
      }
    }

    // Import settings
    for (const setting of data.settings) {
      try {
        if (options.strategy === 'merge') {
          // Check if setting already exists
          const existing = await db.settings
            .where('key')
            .equals(setting.key)
            .first()

          if (existing) {
            if (options.strategy === 'skip') {
              result.skipped++
              continue
            }
            // Update existing setting
            await db.settings.update(existing.id, {
              value: setting.value,
              updatedAt: new Date(setting.updatedAt),
            })
            result.imported.settings++
            continue
          }
        }

        // Add new setting
        const settingToImport = {
          ...setting,
          updatedAt: new Date(setting.updatedAt),
        }
        delete settingToImport.id
        await db.settings.add(settingToImport)
        result.imported.settings++
      } catch (error) {
        result.errors.push(`Setting import error: ${error}`)
      }
    }

    result.success = result.errors.length === 0
    return result
  } catch (error) {
    result.errors.push(`Import failed: ${error}`)
    return result
  }
}

/**
 * Import from JSON file
 */
export async function importFromFile(
  file: File,
  options?: ImportOptions
): Promise<ImportResult> {
  try {
    const text = await file.text()
    const data = JSON.parse(text)
    return importFromJSON(data, options)
  } catch (error) {
    return {
      success: false,
      imported: { tasks: 0, settings: 0 },
      skipped: 0,
      errors: [`Failed to parse file: ${error}`],
    }
  }
}

/**
 * Get backup file size estimate (in MB)
 */
export async function getBackupSizeEstimate(userId?: string): Promise<number> {
  const data = await exportToJSON(userId)
  const json = JSON.stringify(data)
  return json.length / (1024 * 1024) // Convert to MB
}

/**
 * Create auto-backup (stores in localStorage with rotation)
 */
export async function createAutoBackup(userId?: string): Promise<void> {
  const data = await exportToJSON(userId)
  const json = JSON.stringify(data)

  // Store in localStorage with timestamp
  const backupKey = `autoBackup_${new Date().toISOString()}`
  localStorage.setItem(backupKey, json)

  // Keep only last 5 auto-backups (rotation)
  const allBackups = Object.keys(localStorage).filter((key) =>
    key.startsWith('autoBackup_')
  )

  if (allBackups.length > 5) {
    allBackups.sort()
    const toDelete = allBackups.slice(0, allBackups.length - 5)
    toDelete.forEach((key) => localStorage.removeItem(key))
  }
}

/**
 * Get list of auto-backups
 */
export function getAutoBackups(): { key: string; timestamp: Date }[] {
  const backups = Object.keys(localStorage)
    .filter((key) => key.startsWith('autoBackup_'))
    .map((key) => ({
      key,
      timestamp: new Date(key.replace('autoBackup_', '')),
    }))
    .sort((a, b) => b.timestamp.getTime() - a.timestamp.getTime())

  return backups
}

/**
 * Restore from auto-backup
 */
export async function restoreFromAutoBackup(
  backupKey: string,
  options?: ImportOptions
): Promise<ImportResult> {
  const json = localStorage.getItem(backupKey)
  if (!json) {
    return {
      success: false,
      imported: { tasks: 0, settings: 0 },
      skipped: 0,
      errors: ['Backup not found'],
    }
  }

  try {
    const data = JSON.parse(json)
    return importFromJSON(data, options)
  } catch (error) {
    return {
      success: false,
      imported: { tasks: 0, settings: 0 },
      skipped: 0,
      errors: [`Failed to restore backup: ${error}`],
    }
  }
}
